import { FormControl, Grid, InputAdornment, TextField, keyframes } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import { IconButton } from "components/common/Button/IconButton";
import { Copy } from "components/common/SVG/Copy";
import { EyeIcon } from "components/common/SVG/EyeIcon";
import { EyeOffIcon } from "components/common/SVG/EyeOffIcon";
import { ErrorTooltip } from "components/common/Tooltips/Error";
import CustomTooltip from "components/common/Tooltips/Custom";
import Text from "components/common/Typography/Text";
import { FeatureFlagsContext } from "contexts/FeatureFlagsContext";
import useSpeechRecognition from "hooks/useSpeechRecognition";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { isFeatureEnabled, speechToTextFeature } from "utils/featureFlags";
import type { InputFieldProps } from "./index.types";

// Pulsing animation for mic icon when listening
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

// Radiating animation for mic icon when listening (blue ripple effect)
const radiateBlue = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(33, 150, 243, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
  }
`;

const InputField: React.FC<InputFieldProps> = ({
  // Cannot provide both value and defaultValue
  // Set this to make this a controlled component
  value,
  // Set this to make this an uncontrolled component
  defaultValue,
  // The label for the input field. This will be displayed above the input field.
  label,
  // The label for the input field. This will be displayed inside the input field (and will float when the input field is focused).
  inputLabel,
  // The name of the input field. This will be used to identify the input field in the form.
  name,
  // Set this to true to make the input field read-only.
  readonly = false,
  min,
  max,
  disabled = false,
  iconDetails = { iconName: null, IconComponent: null },
  onKeyDown,
  type,
  onChange,
  cols,
  formik,
  showError = false,
  errorMessage,
  autoFocus = false,
  onFocus,
  placeholder,
  sxInputProps,
  helperText = "",
  multiline,
  border = "1px solid #E1E1E1",
  borderRadius = "10px",
  className,
  labelVariant = "bodyS",
  rows,
  // Set custom validators that will run on change. These allow us to add custom validation logic to the input field and control the validation styling.
  validators = [],
  copyable = false,
  showPasswordIcon = false,
  speechEnabled = false, // Default to false, enable via feature flag
  speechDisabledReason,
  onSpeechTranscript,
  speechLanguage = "en-US",
  sx = {},
  ...props
}) => {
  const { iconName, IconComponent, iconProps } = iconDetails;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const micClickingRef = useRef(false);
  const [fieldType, setFieldType] = useState<string | undefined>(type);
  const [fieldError, setFieldError] = useState<string | undefined>((formik?.errors[name] as string) || errorMessage);
  const [fieldTouched, setFieldTouched] = useState<boolean>(!!formik?.touched[name] || showError);

  // Inner value is used to store the value of the input field when no onChange is provided
  const [innerValue, setInnerValue] = useState<string | number | undefined>(value);

  // Get the current field value (for display in TextField)
  const fieldValue = formik && name ? formik.values[name] : innerValue;

  // Feature flag for speech-to-text
  const flagsData = useContext(FeatureFlagsContext);
  const isSpeechFeatureEnabled = isFeatureEnabled(flagsData, speechToTextFeature);
  const shouldEnableSpeech = speechEnabled && isSpeechFeatureEnabled;

  // Track the base value (before interim results) to append transcripts correctly
  const baseValueRef = useRef<string>("");

  // Store callbacks in refs to avoid recreating handleSpeechResult
  const onChangeRef = useRef(onChange);
  const onSpeechTranscriptRef = useRef(onSpeechTranscript);

  // Keep refs up to date
  useEffect(() => {
    onChangeRef.current = onChange;
    onSpeechTranscriptRef.current = onSpeechTranscript;
  }, [onChange, onSpeechTranscript]);

  // Helper function to scroll to end and position cursor
  const scrollToEndAndFocus = useCallback(() => {
    // Use setTimeout to ensure DOM is updated before scrolling
    setTimeout(() => {
      if (inputRef.current) {
        const input = inputRef.current;

        // For textarea/multiline inputs
        if (multiline) {
          // Scroll to bottom
          input.scrollTop = input.scrollHeight;
        }

        // Position cursor at the end
        const length = input.value?.length || 0;
        input.setSelectionRange(length, length);

        // Ensure input is focused
        input.focus();
      }
    }, 0);
  }, [multiline]);

  // Memoized callback for speech recognition to prevent re-render issues
  const handleSpeechResult = useCallback(
    (transcript: string, isFinal: boolean) => {
      if (isFinal) {
        // Final result: append to base value and update base
        const newValue = baseValueRef.current ? `${baseValueRef.current} ${transcript}` : transcript;
        baseValueRef.current = newValue; // Update base for next transcript

        // Update inner value
        setInnerValue(newValue);

        // For Formik fields, update formik
        if (formik && name) {
          formik.setFieldValue(name, newValue);
        }

        // Trigger onChange for any other listeners
        if (onChangeRef.current) {
          onChangeRef.current({
            target: {
              name: name,
              value: newValue,
            },
          } as React.ChangeEvent<HTMLInputElement>);
        }

        // Call the custom callback
        if (onSpeechTranscriptRef.current) {
          onSpeechTranscriptRef.current(transcript);
        }

        // Auto-scroll to end and position cursor after final transcript
        scrollToEndAndFocus();
      } else {
        // Interim result: show live preview by appending to base value (without updating base)
        const previewValue = baseValueRef.current ? `${baseValueRef.current} ${transcript}` : transcript;

        // Update UI immediately for live feedback
        setInnerValue(previewValue);

        // For Formik fields, show interim preview
        if (formik && name) {
          formik.setFieldValue(name, previewValue);
        }

        // Trigger onChange for interim results too
        if (onChangeRef.current) {
          onChangeRef.current({
            target: {
              name: name,
              value: previewValue,
            },
          } as React.ChangeEvent<HTMLInputElement>);
        }

        // Also scroll during interim results for immediate feedback
        scrollToEndAndFocus();
      }
    },
    [formik, name, scrollToEndAndFocus]
  );

  // Generate unique ID for this input to prevent transcript leakage
  const inputIdRef = useRef(`input-${name}-${Math.random().toString(36).substr(2, 9)}`);

  // Speech recognition hook (only when enabled)
  const {
    isListening,
    isActivelyListening, // True only when THIS input is actively listening
    isSupported: isSpeechSupported,
    error: speechError,
    startListening,
    stopListening,
    resetError: resetSpeechError,
    syncTranscriptPosition,
  } = useSpeechRecognition({
    continuous: true, // Keep listening until manually stopped
    interimResults: true, // Enable live transcription as user speaks
    language: speechLanguage,
    inputId: inputIdRef.current, // Unique ID to prevent transcript leakage
    onResult: handleSpeechResult,
  });

  // Update base value ref when the field value changes externally (typing or initial value)
  useEffect(() => {
    if (!isListening) {
      // When not listening, keep base value in sync with actual field value
      baseValueRef.current = String(fieldValue || "");
    }
  }, [fieldValue, isListening]);

  useEffect(() => {
    // Only sync innerValue for non-formik controlled fields
    if (!formik && value !== undefined) {
      setInnerValue(value);
    }
  }, [value, formik]);

  useEffect(() => {
    // Function to get nested values with dot notation or index lookup EX: a.b[0].c or a.b[2][name]
    const getNestedValue = (obj: any, path: string): any => {
      if (!obj || !path) return null;
      return path
        .split(/[.[\]]/)
        .filter((x) => !!x)
        .reduce((acc, part) => {
          if (acc && part) {
            if (part.match(/\d+/)) {
              return acc[parseInt(part)];
            } else {
              return acc[part];
            }
          }
          return null;
        }, obj);
    };

    setFieldTouched(!!getNestedValue(formik?.touched, name) || showError);
    setFieldError(getNestedValue(formik?.errors, name) || errorMessage);
  }, [formik, showError, name, errorMessage]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    setFieldTouched(true);

    // Sync base value on manual edits (including clears) so subsequent speech
    // appends to the current field content, not stale pre-edit content.
    // handleChange is only triggered by keyboard input, never by speech callbacks,
    // so this is safe to do even while listening.
    baseValueRef.current = value;
    if (isActivelyListening) {
      syncTranscriptPosition();
    }

    if (formik) {
      formik.handleChange(event);
    }

    setInnerValue(value);
    validate(value);

    if (onChange) {
      onChange(event);
    }
    if (props.maxLength && value.length > props.maxLength) {
      return;
    }
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.ctrlKey && event.key === "Backspace") {
      // Handle Ctrl + Backspace key combination
      if (onChange) {
        onChange({
          target: {
            name: name,
            value: (event.target as HTMLInputElement).value,
          },
        } as React.ChangeEvent<HTMLInputElement>);
      }
    }
  };

  const validate = (value: string | number | undefined): void => {
    setFieldError(undefined);
    inputRef.current?.setCustomValidity("");
    if (validators.length) {
      for (const validator of validators) {
        const err = validator(value);
        if (err) {
          setFieldError(err);
          inputRef.current?.setCustomValidity(err);
        }
      }
    }
  };

  useEffect(() => {
    validate(innerValue);
  }, []);

  // Check validation on default value
  useEffect(() => {
    if (defaultValue) validate(defaultValue);
  }, [defaultValue]);

  return (
    <Grid size={cols}>
      {label && (
        <Text variant={labelVariant} fontWeight={500}>
          {label}
        </Text>
      )}
      <ErrorTooltip arrow title={fieldTouched && fieldError ? fieldError : ""} placement="right">
        <FormControl
          fullWidth
          variant="outlined"
          className={className}
          onBlur={(event) => {
            if (micClickingRef.current) return;
            if (event.currentTarget.contains(event.relatedTarget as Node)) return;
            stopListening();
          }}>
          <TextField
            id={`${name}`}
            name={name}
            autoFocus={autoFocus}
            defaultValue={defaultValue}
            value={fieldValue}
            type={fieldType}
            disabled={disabled}
            multiline={multiline}
            label={inputLabel}
            rows={rows}
            data-testid={name}
            onBlur={(event) => {
              setFieldTouched(true);
              if (formik?.handleBlur) {
                formik.handleBlur(event);
              }
              if (props.onBlur) {
                props.onBlur(event);
              }
            }}
            onKeyDown={onKeyDown}
            onKeyUp={handleKeyUp}
            autoComplete={"off"}
            onFocus={onFocus}
            size="small"
            error={fieldTouched && !!fieldError}
            helperText={helperText}
            color="info"
            sx={{ border: border, borderRadius: borderRadius, ...sx }}
            placeholder={placeholder}
            {...(type === "date" ? { InputLabelProps: { shrink: true } } : {})}
            onChange={handleChange}
            slotProps={{
              input: {
                style: { ...sxInputProps },
                autoComplete: "off",
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{
                      cursor: "pointer",
                      marginLeft: "0px",
                      marginRight: "0px",
                    }}>
                    {!!IconComponent && <IconComponent {...iconProps} />}
                    {!!iconName && <img alt={iconName} data-testid={`input-field-right-icon`} src={iconName} />}
                    {copyable && (
                      <IconButton
                        variant="text"
                        onClick={() => {
                          navigator.clipboard.writeText(String(innerValue || ""));
                        }}>
                        <Copy />
                      </IconButton>
                    )}
                    {showPasswordIcon && (
                      <IconButton
                        variant="text"
                        onClick={() => {
                          setFieldType(fieldType === "password" ? "text" : "password");
                        }}>
                        {fieldType === "password" ? <EyeOffIcon /> : <EyeIcon />}
                      </IconButton>
                    )}
                    {shouldEnableSpeech && (
                      <span
                        onMouseDown={() => {
                          micClickingRef.current = true;
                          setTimeout(() => {
                            micClickingRef.current = false;
                          }, 0);
                        }}>
                        <CustomTooltip
                          tooltipText={
                            speechDisabledReason
                              ? speechDisabledReason
                              : !isSpeechSupported
                                ? "Speech recognition not supported in this browser"
                                : speechError
                                  ? speechError
                                  : isActivelyListening
                                    ? "Click to stop listening"
                                    : isListening
                                      ? "Microphone is in use by another field"
                                      : "Click to speak"
                          }
                          placement="top">
                          <IconButton
                            variant="text"
                            disabled={!!speechDisabledReason || !isSpeechSupported || disabled || (isListening && !isActivelyListening)}
                            onClick={() => {
                              if (isActivelyListening) {
                                stopListening();
                              } else {
                                resetSpeechError();
                                startListening();
                              }
                            }}
                            sx={{
                              color:
                                speechDisabledReason || !isSpeechSupported || (isListening && !isActivelyListening)
                                  ? "grey"
                                  : speechError
                                    ? "error.main"
                                    : isActivelyListening
                                      ? "#2196F3"
                                      : "primary.main",
                              animation: isActivelyListening
                                ? `${pulse} 1.5s ease-in-out infinite, ${radiateBlue} 2s ease-out infinite`
                                : "none",
                              borderRadius: "50%",
                              position: "relative",
                            }}>
                            {speechDisabledReason || !isSpeechSupported || speechError || (isListening && !isActivelyListening) ? (
                              <MicOffIcon sx={{ height: "20px", width: "20px" }} />
                            ) : (
                              <MicIcon sx={{ height: "20px", width: "20px" }} />
                            )}
                          </IconButton>
                        </CustomTooltip>
                      </span>
                    )}
                  </InputAdornment>
                ),
              },

              htmlInput: {
                ref: inputRef,
                "data-testid": `input-${name}`,
                maxLength: props.maxLength,
                minLength: props.minLength,
                min: min,
                max: max,
                ...(readonly ? { readOnly: true } : {}),
              },
            }}
          />
        </FormControl>
      </ErrorTooltip>
    </Grid>
  );
};

export default InputField;
